import { MODELS } from '../data/models';
import { PROVIDERS } from '../data/providers';
import { t, getCurrentLang } from './i18n';
import { escapeHtml } from './ui-helpers';
import { toast } from './toast';
import { resetSpent } from './cost-tracking';
import { testProvider } from '../api';
import { getKey, saveKey, removeKey, clearAllKeys, setKeyLocal } from './api-keys';
import {
  isCloudAutosaveEnabled, isLocalAutosaveEnabled,
  setCloudAutosaveEnabled, setLocalAutosaveEnabled,
  triggerAutosave,
} from './autosave';

/* =================================================================
   8. Settings drawer
   ================================================================= */
export function renderKeyRows() {
  const wrap = document.getElementById("key-rows");
  if (!wrap) return;
  wrap.innerHTML = "";
  Object.keys(PROVIDERS).forEach(pId => {
    const p = PROVIDERS[pId];
    const stored = getKey(pId);
    const helpText = p.keyHelp[getCurrentLang()] || p.keyHelp.en;
    const row = document.createElement("div");
    row.className = "key-row";
    row.innerHTML = `
      <div class="key-head">
        <span class="key-label">
          <span class="provider-dot ${p.colorClass}"></span>
          ${p.label}
        </span>
        <a class="get-link" href="${p.keyUrl}" target="_blank" rel="noopener noreferrer">
          ${t("settings.getkey")}
          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 9l6-6M5 3h4v4"/>
          </svg>
        </a>
      </div>
      <div class="key-help">${helpText}</div>
      <input type="password" autocomplete="off" spellcheck="false"
             placeholder="${p.keyPlaceholder}"
             data-provider="${pId}"
             value="${stored.replace(/"/g, "&quot;")}">
      <div class="key-row-footer">
        <div class="key-status ${stored ? "ok" : ""}" data-status="${pId}">
          ${stored ? t("settings.set") : t("settings.notset")}
        </div>
        <button class="key-test-btn" data-test="${pId}" ${stored ? "" : "disabled"}>
          ${t("settings.test")}
        </button>
      </div>
      <div class="key-test-result" data-test-result="${pId}"></div>
    `;
    wrap.appendChild(row);
  });
  // Debounce backend writes per provider so each keystroke doesn't fire a
  // PUT. We update the local cache immediately (so reads inside the same
  // tick work) and schedule a single backend save 600ms after the last edit.
  const saveTimers: Record<string, number> = {};
  wrap.querySelectorAll("input[data-provider]").forEach(inp => {
    inp.addEventListener("input", e => {
      const pId = e.target.dataset.provider;
      const val = e.target.value.trim();
      // Immediate local update so reads (runner, testProvider) reflect the
      // new value within the same tick. Backend save is debounced so we
      // don't PUT on every keystroke.
      setKeyLocal(pId, val);
      if (saveTimers[pId]) window.clearTimeout(saveTimers[pId]);
      saveTimers[pId] = window.setTimeout(() => {
        if (val) saveKey(pId, val); else removeKey(pId);
      }, 600);
      const statusEl = wrap.querySelector(`[data-status="${pId}"]`);
      statusEl.textContent = val ? t("settings.set") : t("settings.notset");
      statusEl.classList.toggle("ok", !!val);
      const testBtn = wrap.querySelector(`[data-test="${pId}"]`);
      if (testBtn) testBtn.disabled = !val;
      const resultBox = wrap.querySelector(`[data-test-result="${pId}"]`);
      if (resultBox) resultBox.innerHTML = "";
    });
  });
  wrap.querySelectorAll("[data-test]").forEach(btn => {
    btn.addEventListener("click", async () => {
      const pId = btn.dataset.test;
      const resultBox = wrap.querySelector(`[data-test-result="${pId}"]`);
      btn.disabled = true;
      btn.textContent = t("settings.testing");
      resultBox.innerHTML = "";
      const res = await testProvider(pId);
      btn.disabled = false;
      btn.textContent = t("settings.test");
      if (res.ok) {
        // Highlight which of OUR registered model IDs match
        const ourIds = MODELS.filter(m => m.provider === pId).map(m => m.id);
        const matched = ourIds.filter(id => res.models.includes(id));
        const missing = ourIds.filter(id => !res.models.includes(id));
        const matchSummary = matched.length === ourIds.length
          ? `<span class="ok-text">${t("settings.test.allok", { n: matched.length })}</span>`
          : `<span class="warn-text">${t("settings.test.partial", { ok: matched.length, total: ourIds.length })}</span>`;
        let extra = "";
        if (missing.length > 0) {
          extra = `<div class="missing-row">${t("settings.test.missing")}: ${missing.map(m=>`<code>${escapeHtml(m)}</code>`).join(" · ")}</div>`;
        }
        resultBox.innerHTML = `
          <div class="test-ok">
            ✓ ${t("settings.test.ok", { n: res.models.length })}
            <div class="test-match">${matchSummary}</div>
            ${extra}
            <details class="test-details">
              <summary>${t("settings.test.show")}</summary>
              <div class="model-list">${res.models.map(m=>`<code>${escapeHtml(m)}</code>`).join(" ")}</div>
            </details>
          </div>
        `;
      } else {
        resultBox.innerHTML = `
          <div class="test-err">
            ✗ ${escapeHtml(res.error || "Failed")}${res.status ? ` <span class="status">(HTTP ${res.status})</span>` : ""}
          </div>
        `;
      }
    });
  });
}

export function openDrawer() {
  document.getElementById("drawer").classList.add("open");
  document.getElementById("drawer-backdrop").classList.add("open");
}
export function closeDrawer() {
  document.getElementById("drawer").classList.remove("open");
  document.getElementById("drawer-backdrop").classList.remove("open");
}

export function attachSettingsHandlers() {
  document.getElementById("settings-btn").addEventListener("click", openDrawer);
  document.getElementById("drawer-close").addEventListener("click", closeDrawer);
  document.getElementById("drawer-backdrop").addEventListener("click", closeDrawer);
  document.getElementById("clear-keys").addEventListener("click", async () => {
    await clearAllKeys();
    renderKeyRows();
    toast(t("settings.cleared"));
  });
  document.getElementById("reset-spent").addEventListener("click", () => {
    resetSpent();
    toast(t("settings.spent.reset"));
  });

  // ===== Session autosave toggles =====
  const cloudToggle = document.getElementById("autosave-cloud-toggle") as HTMLInputElement | null;
  const localToggle = document.getElementById("autosave-local-toggle") as HTMLInputElement | null;
  if (cloudToggle) {
    cloudToggle.checked = isCloudAutosaveEnabled();
    cloudToggle.addEventListener("change", () => {
      setCloudAutosaveEnabled(cloudToggle.checked);
      toast(cloudToggle.checked
        ? (t("autosave.cloud_on") || "Cloud autosave enabled — first save will fire on the next interaction.")
        : (t("autosave.cloud_off") || "Cloud autosave disabled."));
      // Eagerly save once so the user gets immediate feedback that it's working.
      if (cloudToggle.checked) triggerAutosave();
    });
  }
  if (localToggle) {
    localToggle.checked = isLocalAutosaveEnabled();
    localToggle.addEventListener("change", () => {
      setLocalAutosaveEnabled(localToggle.checked);
      toast(localToggle.checked
        ? (t("autosave.local_on") || "Local autosave enabled — you'll be asked to pick a file on the next interaction.")
        : (t("autosave.local_off") || "Local autosave disabled."));
      if (localToggle.checked) triggerAutosave();
    });
  }
}
